import { InteractionType } from "@azure/msal-browser";
import { MsalAuthenticationTemplate } from "@azure/msal-react";
import { useEffect, useState } from "react";
import { hasCookieSession } from "./cookieSession";

/**
 * Auth gate for embedded sub-routes that call the enterprise apiClient.
 *
 * Hard-wrapping such a route in MsalAuthenticationTemplate forces an interactive
 * login before anything renders, even when the host already holds a valid cookie
 * session (see the mpd route's current setup, which does NOT use this gate because
 * it calls MyProviderData rather than the enterprise client).
 *
 * This checks the cookie session first; only falls back to MSAL interactive auth
 * when no cookie session is present.
 */
export function EmbeddedAuthGate({
  children,
  loadingLabel = "Checking session...",
}: {
  children: React.ReactNode;
  loadingLabel?: string;
}) {
  const [hasCookie, setHasCookie] = useState<boolean | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    hasCookieSession().then((ok) => {
      if (!cancelled) setHasCookie(ok);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (hasCookie === undefined) {
    return (
      <div className="text-center pt-12">
        <div className="p-5 flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
          <span className="text-lg font-light">{loadingLabel}</span>
        </div>
      </div>
    );
  }

  if (hasCookie) {
    return <>{children}</>;
  }

  return (
    <MsalAuthenticationTemplate interactionType={InteractionType.Popup}>
      {children}
    </MsalAuthenticationTemplate>
  );
}
